import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import LanguageIcon from '@mui/icons-material/Language';
import StorageIcon from '@mui/icons-material/Storage';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AgentProgressCard, { AgentPhase } from './AgentProgressCard';

interface ResearchStep {
  step_id: number;
  status: string;
  query: string;
  searches_done?: number;
  pages_visited?: number;
  tool_calls_done?: number;
  [key: string]: unknown;
}

interface DeepResearchProgressProps {
  phase: string;
  progress: number;
  steps: Record<number, ResearchStep>;
  inputTokens?: number;
  outputTokens?: number;
  startedAt?: number;
}

const PHASES: AgentPhase[] = [
  { key: 'planning', label: 'Plano' },
  { key: 'researching', label: 'Pesquisas' },
  { key: 'reviewing', label: 'Revisão' },
  { key: 'synthesizing', label: 'Relatório' },
];

const STEP_STATUS_VERB: Record<string, string> = {
  searching: 'Pesquisando',
  analyzing: 'Analisando',
};

const formatTokenCount = (n: number): string => {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return n.toString();
};

const DeepResearchProgress: React.FC<DeepResearchProgressProps> = ({
  phase,
  progress,
  steps,
  inputTokens = 0,
  outputTokens = 0,
  startedAt,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { totalSearches, totalPages, totalToolCalls } = useMemo(() => {
    let searches = 0;
    let pages = 0;
    let toolCalls = 0;
    for (const stepData of Object.values(steps)) {
      searches += stepData.searches_done || 0;
      pages += stepData.pages_visited || 0;
      toolCalls += stepData.tool_calls_done || 0;
    }
    return { totalSearches: searches, totalPages: pages, totalToolCalls: toolCalls };
  }, [steps]);

  const isDone = phase === 'done';
  const isInterrupted = phase === 'interrupted';
  const activeIndex = Math.max(0, PHASES.findIndex((p) => p.key === phase));
  const stepArr = Object.values(steps);
  const currentStep = stepArr.find((s) => s.active === true)
    || [...stepArr].reverse().find((s) => s.status && s.status !== 'done');
  const doneSteps = stepArr.filter((s) => s.status === 'done').length;
  const hasStats = totalSearches > 0 || totalPages > 0 || totalToolCalls > 0 || inputTokens > 0;

  return (
    <AgentProgressCard
      icon={isDone
        ? <CheckCircleIcon style={{ fontSize: 17, color: '#047857' }} />
        : <TravelExploreIcon style={{ fontSize: 17 }} />}
      title={isDone ? 'Pesquisa aprofundada concluída' : isInterrupted ? 'Pesquisa aprofundada interrompida' : 'Fazendo a pesquisa aprofundada'}
      phases={PHASES}
      activeIndex={isDone ? PHASES.length : activeIndex}
      progress={progress}
      done={isDone}
      error={isInterrupted}
      startedAt={startedAt}
      onHeaderClick={hasStats ? () => setExpanded((v) => !v) : undefined}
      headerExtra={hasStats ? (
        <Stats>
          {totalSearches > 0 && <span><SearchIcon style={{ fontSize: 12 }} /> {totalSearches}</span>}
          {totalPages > 0 && <span><LanguageIcon style={{ fontSize: 12 }} /> {totalPages}</span>}
          {totalToolCalls > 0 && <span><StorageIcon style={{ fontSize: 12 }} /> {totalToolCalls}</span>}
          {(inputTokens > 0 || outputTokens > 0) && (
            <>
              <span>↑ {formatTokenCount(inputTokens)}</span>
              <span>↓ {formatTokenCount(outputTokens)}</span>
            </>
          )}
          {expanded ? <ExpandLessIcon style={{ fontSize: 15 }} /> : <ExpandMoreIcon style={{ fontSize: 15 }} />}
        </Stats>
      ) : undefined}
    >
      {!isDone && currentStep?.query && (
        <CurrentLine>
          <CurrentVerb>{STEP_STATUS_VERB[currentStep.status] || 'Executando'}:</CurrentVerb>
          <CurrentQuery>“{currentStep.query}”</CurrentQuery>
          {stepArr.length > 1 && (
            <StepCount>etapa {Math.min(doneSteps + 1, stepArr.length)}/{stepArr.length}</StepCount>
          )}
        </CurrentLine>
      )}
      {expanded && (
        <Details>
          <DetailLine>
            <DetailIcon><SearchIcon style={{ fontSize: 13 }} /></DetailIcon>
            <DetailLabel><strong>{totalSearches}</strong> buscas</DetailLabel>
            <DetailDesc>consultas feitas a buscadores na web</DetailDesc>
          </DetailLine>
          <DetailLine>
            <DetailIcon><LanguageIcon style={{ fontSize: 13 }} /></DetailIcon>
            <DetailLabel><strong>{totalPages}</strong> páginas</DetailLabel>
            <DetailDesc>páginas abertas e lidas em profundidade</DetailDesc>
          </DetailLine>
          <DetailLine>
            <DetailIcon><StorageIcon style={{ fontSize: 13 }} /></DetailIcon>
            <DetailLabel><strong>{totalToolCalls}</strong> consultas</DetailLabel>
            <DetailDesc>chamadas a bases internas e ferramentas auxiliares</DetailDesc>
          </DetailLine>
          <DetailLine>
            <DetailIcon>↑</DetailIcon>
            <DetailLabel><strong>{formatTokenCount(inputTokens)}</strong> tokens de entrada</DetailLabel>
            <DetailDesc>texto enviado ao modelo durante a pesquisa</DetailDesc>
          </DetailLine>
          <DetailLine>
            <DetailIcon>↓</DetailIcon>
            <DetailLabel><strong>{formatTokenCount(outputTokens)}</strong> tokens de saída</DetailLabel>
            <DetailDesc>texto gerado pelo modelo durante a pesquisa</DetailDesc>
          </DetailLine>
        </Details>
      )}
    </AgentProgressCard>
  );
};

export default DeepResearchProgress;

const Stats = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  opacity: 0.7;
  font-size: 0.78rem;
  & > span { display: inline-flex; align-items: center; gap: 3px; }
`;

const CurrentLine = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 0.8rem;
  min-width: 0;
`;

const CurrentVerb = styled.span`
  opacity: 0.6;
  flex-shrink: 0;
`;

const CurrentQuery = styled.span`
  opacity: 0.85;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StepCount = styled.span`
  opacity: 0.5;
  flex-shrink: 0;
  font-size: 0.75rem;
`;

const Details = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 0.8rem;
  opacity: 0.85;
`;

const DetailLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 7px;
`;

const DetailIcon = styled.span`
  display: inline-flex;
  align-items: center;
  width: 16px;
  justify-content: center;
  opacity: 0.7;
`;

const DetailLabel = styled.span`
  white-space: nowrap;
`;

const DetailDesc = styled.span`
  opacity: 0.6;
`;
